import { useTranslation } from 'react-i18next';
import { SearchAutocomplete, type SearchAutocompleteOption } from '@/shared/ui/components/form/SearchAutocomplete';
import { Text } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { Cross2Icon, Link2Icon } from '@radix-ui/react-icons';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';
import type { UnitRowData } from './schema';
import { FieldRow, ComboField, SelectField } from './UnitFieldPrimitives';

/**
 * Formats a numeric string as BRL currency display (e.g. "450000" → "450.000").
 */
function formatBrl(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  return Number(digits).toLocaleString('pt-BR');
}

export const TYPOLOGY_OPTIONS = [
  'Studio', 'Kitnet', '1 Quarto', '2 Quartos', '3 Quartos',
  '4 Quartos', '5+ Quartos', 'Cobertura', 'Duplex', 'Triplex',
  'Loja', 'Sala Comercial', 'Galpão', 'Terreno',
];

interface AddUnitFormProps {
  draft: UnitRowData;
  propertyOptions: SearchAutocompleteOption[];
  usedTowers: string[];
  usedTypologies: string[];
  onUpdate: (field: keyof UnitRowData, value: string) => void;
  onPropertySelect: (id: string | null) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Inline form card for adding a new unit to a launch.
 */
export function AddUnitForm({ draft, propertyOptions, usedTowers, usedTypologies, onUpdate, onPropertySelect, onConfirm, onCancel }: AddUnitFormProps) {
  const { t } = useTranslation('features/launches');

  return (
    <div className={cn(
      'rounded-(--radius-lg) border border-[var(--seed-base)]',
      'bg-[var(--surface-secondary)] p-[var(--space-4)]',
      'flex flex-col gap-[var(--space-3)]',
    )}>
      <div className="flex items-center justify-between">
        <Text as="span" className="text-[length:var(--text-body-sm)] font-[var(--font-semibold)] text-[var(--text-primary)]">
          {t('create.newUnitTitle')}
        </Text>
        <button type="button" onClick={onCancel} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
          <Cross2Icon width={14} height={14} />
        </button>
      </div>

      <PropertyLinkField
        propertyOptions={propertyOptions}
        propertyId={draft.property_id}
        propertyLabel={draft.property_label}
        onSelect={onPropertySelect}
      />

      <div className="flex flex-col gap-[var(--space-3)]">
        <FieldRow
          label={`${t('create.unitLabelField')} *`}
          placeholder={t('create.unitLabelPlaceholder')}
          value={draft.label}
          onChange={(v) => onUpdate('label', v)}
          maxLength={FORM_FIELD_LIMITS.unitLabel}
          hint={t('create.unitLabelHint')}
        />

        <div className="grid grid-cols-2 gap-[var(--space-3)]">
          <ComboField label={t('create.unitTowerField')} placeholder={t('create.unitTowerPlaceholder')} value={draft.tower} suggestions={usedTowers} onChange={(v) => onUpdate('tower', v.slice(0, FORM_FIELD_LIMITS.unitTower))} />
          <FieldRow label={t('create.unitFloorField')} placeholder={t('create.unitFloorPlaceholder')} value={draft.floor} onChange={(v) => onUpdate('floor', v)} maxLength={FORM_FIELD_LIMITS.unitFloor} />
        </div>

        <div className="grid grid-cols-2 gap-[var(--space-3)]">
          <SelectField label={t('create.unitTypologyField')} placeholder={t('create.selectPlaceholder')} value={draft.typology} options={TYPOLOGY_OPTIONS} extraOptions={usedTypologies} onChange={(v) => onUpdate('typology', v)} />
          <FieldRow label={t('create.unitPriceField')} placeholder={t('create.unitPricePlaceholder')} value={formatBrl(draft.list_price)} onChange={(v) => onUpdate('list_price', v.replace(/\D/g, '').slice(0, FORM_FIELD_LIMITS.moneyMaxDigits))} maxLength={FORM_FIELD_LIMITS.moneyMaxDigits + 4} />
        </div>
      </div>

      <FormActions onConfirm={onConfirm} onCancel={onCancel} disabled={!draft.label} />
    </div>
  );
}

/**
 * Renders the property-linking autocomplete field for a unit row.
 */
function PropertyLinkField({ propertyOptions, propertyId, propertyLabel, onSelect }: {
  propertyOptions: SearchAutocompleteOption[]; propertyId: string; propertyLabel: string;
  onSelect: (id: string | null) => void;
}) {
  const { t } = useTranslation('features/launches');

  return (
    <div className="flex flex-col gap-[2px]">
      <Text as="label" className="text-[length:var(--text-meta)] font-[var(--font-medium)] text-[var(--text-tertiary)]">
        {t('create.linkExistingProperty')}
      </Text>
      <SearchAutocomplete
        placeholder={t('create.searchPropertyPlaceholder')}
        options={propertyOptions}
        value={propertyId || null}
        onChange={(val) => onSelect(val ?? null)}
      />
      {propertyId && (
        <span className="flex items-center gap-[var(--space-1)] text-[length:var(--text-meta)] text-[var(--launch-status-sold-fg)] mt-[var(--space-1)]">
          <Link2Icon width={12} height={12} />
          {t('create.linkedTo', { label: propertyLabel })}
        </span>
      )}
    </div>
  );
}

/**
 * Renders confirm and cancel action buttons for the unit form.
 */
function FormActions({ onConfirm, onCancel, disabled }: { onConfirm: () => void; onCancel: () => void; disabled: boolean }) {
  const { t } = useTranslation('features/launches');

  return (
    <div className="flex items-center gap-[var(--space-3)] pt-[var(--space-1)]">
      <button
        type="button"
        onClick={onConfirm}
        disabled={disabled}
        className={cn(
          'rounded-(--radius-md) px-[var(--space-4)] py-[var(--space-2)]',
          'bg-[var(--seed-base)] text-[var(--text-inverse)]',
          'text-[length:var(--text-small)] font-[var(--font-medium)]',
          'hover:opacity-[0.9] disabled:opacity-50 disabled:cursor-not-allowed',
        )}
        style={{ transition: 'var(--transition-fast)' }}
      >
        {t('create.confirmUnit')}
      </button>
      <button type="button" onClick={onCancel} className="text-[length:var(--text-small)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
        {t('confirm.cancel')}
      </button>
    </div>
  );
}

/**
 * Compact card representing an already-added unit.
 */
export function UnitCard({ unit, onRemove }: { unit: UnitRowData; onRemove: () => void }) {
  const { t } = useTranslation('features/launches');
  const details = [unit.tower, unit.floor, unit.typology].filter(Boolean).join(' · ');

  return (
    <div className={cn(
      'flex items-center justify-between',
      'rounded-(--radius-md) border border-[var(--border-subtle)]',
      'bg-[var(--surface-primary)] px-[var(--space-3)] py-[var(--space-3)]',
      'shadow-[var(--shadow-sm)]',
    )}>
      <div className="flex flex-col gap-[1px] min-w-0">
        <div className="flex items-center gap-[var(--space-2)]">
          <Text as="span" className="text-[length:var(--text-body-sm)] font-[var(--font-semibold)] text-[var(--text-primary)] truncate">
            {unit.label}
          </Text>
          {unit.list_price && (
            <Text
              as="span"
              className="text-[length:var(--text-meta)] text-[var(--launch-status-sold-solid)] font-[var(--font-medium)]"
            >
              R$ {formatBrl(unit.list_price)}
            </Text>
          )}
        </div>
        <div className="flex items-center gap-[var(--space-2)]">
          {details && (
            <Text
              as="span"
              className="text-[length:var(--text-meta)] text-[var(--text-tertiary)] truncate"
            >
              {details}
            </Text>
          )}
          {unit.property_id && (
            <span className="flex items-center gap-[2px] text-[length:var(--text-meta)] text-[var(--seed-base)]">
              <Link2Icon width={10} height={10} />
              {t('create.linked')}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('create.removeUnit')}
        className="shrink-0 text-[var(--text-tertiary)] hover:text-[var(--status-error)] p-[var(--space-1)]"
      >
        <Cross2Icon width={14} height={14} />
      </button>
    </div>
  );
}
